import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { fetchProcessTracking, type ProcessTrackingRow } from './icmTrackingApi';
import '../../backlog/backlog.css';
import '../alerts/icmAlerts.css';

const FORM_TITLE = 'Item Process Tracking History';

/**
 * Inventory Management → Tracking → Item Process Tracking.
 *
 * Every requisition, regardless of status — the full requisition → order →
 * receive → distribute lifecycle in one grid, widened with the linked lot's
 * Distribution columns. Ported from ALPACA `Requisition_ListView_History`
 * (no criteria), which is read-only. A row-selection checkbox column is
 * shown here on user request (2026-09-22) for consistency with the other
 * Tracking grids.
 */
export default function ItemProcessTrackingPage() {
  const [rows, setRows] = useState<ProcessTrackingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const page = await fetchProcessTracking();
      setRows(page.rows);
      setTotal(page.total);
      setSelected(new Set());
    } catch {
      setError('Failed to load item process tracking.');
      setRows([]); setTotal(0);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.rqid ?? '').toLowerCase().includes(t) ||
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.itemCode ?? '').toLowerCase().includes(t) ||
      (r.lt ?? '').toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  /** G5 titles this form "Item Process Tracking History": its view id contains "History",
   *  and ViewCaptionChangeController appends the word for such views. The sidebar entry
   *  keeps the shorter name, as G5's does. Uses the shared `ai:topbar-title` override. */
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: FORM_TITLE }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, []);

  // Column set, order and captions from the LIVE ALPACA model (Requisition_ListView_History):
  // Date · Item · Description · Catalog# · Vendor · RQ# · RC# · PO# · LT# ·
  // VendorLT · ExpiryDate · Units · Storage · Department · RequestedBy · ReviewedDate ·
  // ReviewedBy · PurchaseDate · Purchaseby · ReceiveDate · ReceivedBy · DistributionDate ·
  // GivenBy · Status. "Purchaseby" is G5's own spelling, and Specification is captioned
  // "Description" on the Items BO.
  const cols: AppColumn<ProcessTrackingRow>[] = [
    { field: 'requestedDate',    header: 'Date',        width: 150, type: 'date', text: r => fmtLocalDateTime(r.requestedDate) ?? '—', render: r => fmtLocalDateTime(r.requestedDate) ?? '—' },
    { field: 'itemName',         header: 'Item',        width: 200, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'specification',    header: 'Description',   width: 200, text: r => r.specification ?? '—', render: r => r.specification ?? '—' },
    { field: 'catalog',          header: 'Catalog#',    width: 110, text: r => r.catalog ?? '—', render: r => r.catalog ?? '—' },
    { field: 'vendorName',       header: 'Vendor',      width: 140, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    { field: 'rqid',             header: 'RQ#',         width: 110, text: r => r.rqid ?? '—', render: r => r.rqid ?? '—' },
    { field: 'receiveId',        header: 'RC#',         width: 110, text: r => r.receiveId ?? '—', render: r => r.receiveId ?? '—' },
    { field: 'purchaseOrderNo',  header: 'PO#',         width: 110, text: r => r.purchaseOrderNo ?? '—', render: r => r.purchaseOrderNo ?? '—' },
    { field: 'lt',               header: 'LT#',         width: 110, text: r => r.lt ?? '—', render: r => r.lt ?? '—' },
    { field: 'vendorLt',         header: 'VendorLT',    width: 110, text: r => r.vendorLt ?? '—', render: r => r.vendorLt ?? '—' },
    { field: 'expiryDate',       header: 'ExpiryDate',      width: 130, type: 'date', text: r => fmtLocalDateTime(r.expiryDate) ?? '—', render: r => fmtLocalDateTime(r.expiryDate) ?? '—' },
    { field: 'packUnit',         header: 'Units',       width: 100, text: r => r.packUnit ?? '—', render: r => r.packUnit ?? '—' },
    { field: 'storageName',      header: 'Storage',     width: 120, text: r => r.storageName ?? '—', render: r => r.storageName ?? '—' },
    { field: 'departmentName',   header: 'Department',  width: 130, text: r => r.departmentName ?? '—', render: r => r.departmentName ?? '—' },
    { field: 'requestedByName',  header: 'RequestedBy',  width: 140, text: r => r.requestedByName ?? '—', render: r => r.requestedByName ?? '—' },
    { field: 'reviewedDate',     header: 'ReviewedDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.reviewedDate) ?? '—', render: r => fmtLocalDateTime(r.reviewedDate) ?? '—' },
    { field: 'reviewedByName',   header: 'ReviewedBy',  width: 140, text: r => r.reviewedByName ?? '—', render: r => r.reviewedByName ?? '—' },
    { field: 'purchaseDate',     header: 'PurchaseDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.purchaseDate) ?? '—', render: r => fmtLocalDateTime(r.purchaseDate) ?? '—' },
    { field: 'purchaseByName',   header: 'Purchaseby',   width: 140, text: r => r.purchaseByName ?? '—', render: r => r.purchaseByName ?? '—' },
    { field: 'receiveDate',      header: 'ReceiveDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.receiveDate) ?? '—', render: r => fmtLocalDateTime(r.receiveDate) ?? '—' },
    { field: 'receivedByName',   header: 'ReceivedBy',  width: 140, text: r => r.receivedByName ?? '—', render: r => r.receivedByName ?? '—' },
    { field: 'distributionDate', header: 'DistributionDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.distributionDate) ?? '—', render: r => fmtLocalDateTime(r.distributionDate) ?? '—' },
    { field: 'givenByName',      header: 'GivenBy',     width: 140, text: r => r.givenByName ?? '—', render: r => r.givenByName ?? '—' },
    { field: 'statusLabel',      header: 'Status',      width: 150, type: 'status', text: r => r.statusLabel ?? '—', render: r => r.statusLabel ?? '—' },
  ];

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Inventory Management</span><i className="ti ti-chevron-right" />
            <span>Tracking</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{FORM_TITLE}</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-process-tracking" title={FORM_TITLE} loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'item-process-tracking', ...gridExportMatrix(filtered, cols) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search RQ#, LT#, item, vendor…"
              data-unsaved-ignore
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <span className="ica-note">{total.toLocaleString()} requisition(s)</span>
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<ProcessTrackingRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              defaultPageSize={20}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              emptyText="No requisitions found."
            />
          )}
        </div>
      </div>
    </div>
  );
}
